import { useEffect, useState } from 'react';
import { strings } from '@/content/strings';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import styles from './SignalMeter.module.css';

interface SignalMeterProps {
  /** The floor's mastery estimate (0-1, mastery.ts). */
  value: number;
  /** Where the bar starts before growing (or shrinking) to `value`, e.g. at the mission start. */
  from?: number | undefined;
  className?: string | undefined;
}

/**
 * The "r signal" meter: the floor's mastery estimate as a bar. Honest: it rises with real
 * progress and may dip (docs/research/rewards-and-level-model.md).
 */
export function SignalMeter({ value, from, className }: SignalMeterProps) {
  const [shown, setShown] = useState(from ?? value);
  useEffect(() => {
    // One frame at `from` first, so the change animates.
    const id = requestAnimationFrame(() => {
      setShown(value);
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [value]);
  return (
    <span
      className={cx(styles.root, className)}
      role="meter"
      aria-label={strings.codename.signal}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
    >
      <span className={styles.label} aria-hidden>
        {strings.codename.signal}
      </span>
      <span className={styles.track} aria-hidden>
        <span className={styles.fill} style={cssVars({ '--signal': shown.toFixed(3) })} />
      </span>
    </span>
  );
}
